<template>
  <div class="admin-convenios">
    <div class="d-flex justify-content-between align-items-center mb-2">
      <div>
        <h4 class="mb-25">Convenios</h4>
        <small class="text-muted">Gestiona los convenios y sus categorias laborales</small>
      </div>
      <b-button variant="primary" @click="abrirModalNuevoConvenio">
        <feather-icon icon="PlusIcon" class="mr-50" />
        Nuevo Convenio
      </b-button>
    </div>

    <b-row>
      <!-- Panel izquierdo: Lista de convenios -->
      <b-col md="5">
        <b-table
          :items="convenios"
          :fields="camposConvenios"
          responsive
          show-empty
          empty-text="No hay convenios"
          selectable
          select-mode="single"
          @row-selected="seleccionarConvenio"
          :tbody-tr-class="rowClass"
        >
          <template #cell(acciones)="data">
            <div class="text-nowrap">
              <b-button
                variant="flat-primary"
                size="sm"
                class="btn-icon mr-50"
                @click.stop="editarConvenio(data.item)"
                v-b-tooltip.hover
                title="Editar"
              >
                <feather-icon icon="EditIcon" />
              </b-button>
              <b-button
                variant="flat-danger"
                size="sm"
                class="btn-icon"
                @click.stop="eliminarConvenioConfirm(data.item.id)"
                v-b-tooltip.hover
                title="Eliminar"
              >
                <feather-icon icon="TrashIcon" />
              </b-button>
            </div>
          </template>
        </b-table>
      </b-col>

      <!-- Panel derecho: Categorias del convenio seleccionado -->
      <b-col md="7">
        <div v-if="convenioSeleccionado">
          <div class="d-flex justify-content-between align-items-center mb-1">
            <h5 class="mb-0">Categorias de: {{ convenioSeleccionado.nombre }}</h5>
            <b-button variant="outline-primary" size="sm" @click="abrirModalNuevaCategoria">
              <feather-icon icon="PlusIcon" class="mr-50" />
              Añadir Categoria
            </b-button>
          </div>

          <b-table
            :items="categoriasConvenio"
            :fields="camposCategorias"
            responsive
            show-empty
            empty-text="No hay categorias"
          >
            <template #cell(tipo)="data">
              <b-badge :variant="data.item.tipo === 'dias' ? 'light-primary' : 'light-info'">
                {{ data.item.tipo === 'dias' ? 'Dias' : 'Horas' }}
              </b-badge>
            </template>

            <template #cell(acciones)="data">
              <div class="text-nowrap">
                <b-button
                  variant="flat-primary"
                  size="sm"
                  class="btn-icon mr-50"
                  @click="editarCategoria(data.item)"
                  v-b-tooltip.hover
                  title="Editar"
                >
                  <feather-icon icon="EditIcon" />
                </b-button>
                <b-button
                  variant="flat-danger"
                  size="sm"
                  class="btn-icon"
                  @click="eliminarCategoriaConfirm(data.item.id)"
                  v-b-tooltip.hover
                  title="Eliminar"
                >
                  <feather-icon icon="TrashIcon" />
                </b-button>
              </div>
            </template>
          </b-table>
        </div>

        <b-alert v-else show variant="info" class="mt-1">
          Selecciona un convenio para ver sus categorias.
        </b-alert>
      </b-col>
    </b-row>

    <!-- Modal Crear/Editar Convenio -->
    <b-modal
      v-model="mostrarModalConvenio"
      :title="convenioEditar ? 'Editar Convenio' : 'Nuevo Convenio'"
      ok-title="Guardar"
      cancel-title="Cancelar"
      centered
      @ok="guardarConvenio"
    >
      <b-form-group label="Nombre" label-for="convenio-nombre">
        <b-form-input id="convenio-nombre" v-model="formularioConvenio.nombre" placeholder="Ej: Convenio Textil" />
      </b-form-group>
    </b-modal>

    <!-- Modal Crear/Editar Categoria -->
    <b-modal
      v-model="mostrarModalCategoria"
      :title="categoriaEditar ? 'Editar Categoria' : 'Nueva Categoria'"
      ok-title="Guardar"
      cancel-title="Cancelar"
      centered
      @ok="guardarCategoria"
    >
      <b-form-group label="Nombre" label-for="cat-nombre">
        <b-form-input id="cat-nombre" v-model="formularioCategoria.nombre" placeholder="Ej: Oficial de 1a" />
      </b-form-group>

      <b-form-group label="Cantidad" label-for="cat-cantidad">
        <b-form-input id="cat-cantidad" v-model.number="formularioCategoria.cantidad" type="number" />
      </b-form-group>

      <b-form-group label="Tipo" label-for="cat-tipo">
        <b-form-select id="cat-tipo" v-model="formularioCategoria.tipo" :options="opcionesTipo" />
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import {
  BRow, BCol, BTable, BBadge, BButton, BModal, BFormGroup, BFormInput, BFormSelect, BAlert,
  VBTooltip
} from 'bootstrap-vue'
import { mapState, mapActions } from 'vuex'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BRow,
    BCol,
    BTable,
    BBadge,
    BButton,
    BModal,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BAlert,
  },

  directives: {
    'b-tooltip': VBTooltip,
  },

  data() {
    return {
      convenioSeleccionado: null,
      mostrarModalConvenio: false,
      mostrarModalCategoria: false,
      convenioEditar: null,
      categoriaEditar: null,
      formularioConvenio: {
        nombre: '',
      },
      formularioCategoria: {
        nombre: '',
        cantidad: null,
        tipo: 'dias',
      },
      camposConvenios: [
        { key: 'nombre', label: 'Nombre' },
        { key: 'total_categorias', label: 'Categorias', class: 'text-center' },
        { key: 'acciones', label: '', class: 'text-center', thStyle: { width: '120px' } },
      ],
      camposCategorias: [
        { key: 'nombre', label: 'Nombre' },
        { key: 'cantidad', label: 'Cantidad', class: 'text-center' },
        { key: 'tipo', label: 'Tipo', class: 'text-center' },
        { key: 'acciones', label: '', class: 'text-center', thStyle: { width: '120px' } },
      ],
      opcionesTipo: [
        { value: 'dias', text: 'Dias' },
        { value: 'horas', text: 'Horas' },
      ],
    }
  },

  computed: {
    ...mapState('vacaciones', ['convenios', 'categoriasConvenio']),
  },

  mounted() {
    this.fetchConvenios()
  },

  methods: {
    ...mapActions('vacaciones', [
      'fetchConvenios', 'crearConvenio', 'actualizarConvenio', 'eliminarConvenio',
      'fetchCategorias', 'crearCategoria', 'actualizarCategoria', 'eliminarCategoria',
    ]),

    rowClass(item) {
      if (this.convenioSeleccionado && item.id === this.convenioSeleccionado.id) {
        return 'table-primary'
      }
      return ''
    },

    async seleccionarConvenio(rows) {
      if (rows.length > 0) {
        this.convenioSeleccionado = rows[0]
        await this.fetchCategorias(rows[0].id)
      }
    },

    // Convenios
    abrirModalNuevoConvenio() {
      this.convenioEditar = null
      this.formularioConvenio = { nombre: '' }
      this.mostrarModalConvenio = true
    },

    editarConvenio(convenio) {
      this.convenioEditar = convenio
      this.formularioConvenio = { nombre: convenio.nombre }
      this.mostrarModalConvenio = true
    },

    async guardarConvenio(bvModalEvt) {
      bvModalEvt.preventDefault()

      if (!this.formularioConvenio.nombre) {
        this.$toast({
          component: ToastificationContent,
          props: { title: 'El nombre es requerido', icon: 'AlertTriangleIcon', variant: 'warning' },
        })
        return
      }

      try {
        if (this.convenioEditar) {
          await this.actualizarConvenio({ id: this.convenioEditar.id, ...this.formularioConvenio })
        } else {
          await this.crearConvenio(this.formularioConvenio)
        }
        this.$toast({
          component: ToastificationContent,
          props: { title: 'Convenio guardado', icon: 'CheckIcon', variant: 'success' },
        })
        this.mostrarModalConvenio = false
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: { title: (error && error.error) || 'Error al guardar', icon: 'AlertTriangleIcon', variant: 'danger' },
        })
      }
    },

    eliminarConvenioConfirm(id) {
      this.$bvModal.msgBoxConfirm('¿Eliminar este convenio? Se eliminaran tambien sus categorias.', {
        title: 'Confirmar eliminacion',
        okVariant: 'danger',
        okTitle: 'Si, eliminar',
        cancelTitle: 'Cancelar',
        centered: true,
      }).then(async value => {
        if (value) {
          try {
            await this.eliminarConvenio(id)
            if (this.convenioSeleccionado && this.convenioSeleccionado.id === id) {
              this.convenioSeleccionado = null
            }
            this.$toast({
              component: ToastificationContent,
              props: { title: 'Convenio eliminado', icon: 'CheckIcon', variant: 'success' },
            })
          } catch (error) {
            this.$toast({
              component: ToastificationContent,
              props: { title: (error && error.error) || 'Error al eliminar', icon: 'AlertTriangleIcon', variant: 'danger' },
            })
          }
        }
      })
    },

    // Categorias
    abrirModalNuevaCategoria() {
      this.categoriaEditar = null
      this.formularioCategoria = { nombre: '', cantidad: null, tipo: 'dias' }
      this.mostrarModalCategoria = true
    },

    editarCategoria(categoria) {
      this.categoriaEditar = categoria
      this.formularioCategoria = {
        nombre: categoria.nombre,
        cantidad: categoria.cantidad,
        tipo: categoria.tipo,
      }
      this.mostrarModalCategoria = true
    },

    async guardarCategoria(bvModalEvt) {
      bvModalEvt.preventDefault()

      if (!this.formularioCategoria.nombre) {
        this.$toast({
          component: ToastificationContent,
          props: { title: 'El nombre es requerido', icon: 'AlertTriangleIcon', variant: 'warning' },
        })
        return
      }

      try {
        if (this.categoriaEditar) {
          await this.actualizarCategoria({
            id: this.categoriaEditar.id,
            convenio_id: this.convenioSeleccionado.id,
            ...this.formularioCategoria,
          })
        } else {
          await this.crearCategoria({
            convenio_id: this.convenioSeleccionado.id,
            ...this.formularioCategoria,
          })
        }
        this.$toast({
          component: ToastificationContent,
          props: { title: 'Categoria guardada', icon: 'CheckIcon', variant: 'success' },
        })
        this.mostrarModalCategoria = false
        // Refresh convenios to update total_categorias count
        await this.fetchConvenios()
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: { title: (error && error.error) || 'Error al guardar', icon: 'AlertTriangleIcon', variant: 'danger' },
        })
      }
    },

    eliminarCategoriaConfirm(id) {
      this.$bvModal.msgBoxConfirm('¿Eliminar esta categoria?', {
        title: 'Confirmar eliminacion',
        okVariant: 'danger',
        okTitle: 'Si, eliminar',
        cancelTitle: 'Cancelar',
        centered: true,
      }).then(async value => {
        if (value) {
          try {
            await this.eliminarCategoria({ id, convenio_id: this.convenioSeleccionado.id })
            await this.fetchConvenios()
            this.$toast({
              component: ToastificationContent,
              props: { title: 'Categoria eliminada', icon: 'CheckIcon', variant: 'success' },
            })
          } catch (error) {
            this.$toast({
              component: ToastificationContent,
              props: { title: (error && error.error) || 'Error al eliminar', icon: 'AlertTriangleIcon', variant: 'danger' },
            })
          }
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.admin-convenios {
  .btn-icon {
    padding: 0.5rem;

    &:hover {
      transform: scale(1.1);
    }
  }
}
</style>
